import { Plus, X } from 'lucide-react';

import {
  type AvailabilityDay,
  type AvailabilityWindow,
  dayWindowsError,
  nextWindowAfter,
  type Weekday,
  weekdays,
  type WeeklyAvailability,
} from '@/modules/availability/preferences';
import { Button } from '@/modules/ui/components/button/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/ui/components/select/Select';
import { Switch } from '@/modules/ui/components/switch/Switch';

const weekdayLabels: Record<Weekday, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

/** 00:00–23:30 in half-hour steps, the grid interviews are offered on. */
const timeOptions = Array.from({ length: 48 }, (_, index) => {
  const hours = String(Math.floor(index / 2)).padStart(2, '0');
  const minutes = index % 2 === 0 ? '00' : '30';
  return `${hours}:${minutes}`;
});

// A window saved off the half-hour grid stays selectable, so opening the
// editor never silently rewrites hours the hiring manager did not touch.
const optionsFor = (time: string) =>
  timeOptions.includes(time)
    ? timeOptions
    : [time, ...timeOptions].sort((a, b) => a.localeCompare(b));

interface TimeSelectProps {
  'aria-label': string;
  disabled?: boolean;
  onChange: (time: string) => void;
  value: string;
}

function TimeSelect({
  'aria-label': ariaLabel,
  disabled,
  onChange,
  value,
}: TimeSelectProps) {
  return (
    <Select disabled={disabled} onValueChange={onChange} value={value}>
      <SelectTrigger
        aria-label={ariaLabel}
        className="w-[88px] text-[13px]"
        size="sm"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {optionsFor(value).map((time) => (
          <SelectItem key={time} value={time}>
            {time}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface WeeklyWindowsEditorProps {
  disabled?: boolean;
  onChange: (weeklyAvailability: WeeklyAvailability) => void;
  value: WeeklyAvailability;
}

/**
 * The recurring weekly windows Finlay may offer candidates. Every change is
 * reported up; the parent owns saving, so an invalid day can be shown here
 * without being sent.
 */
export function WeeklyWindowsEditor({
  disabled,
  onChange,
  value,
}: WeeklyWindowsEditorProps) {
  const updateDay = (weekday: Weekday, day: AvailabilityDay) =>
    onChange({ ...value, [weekday]: day });

  return (
    <div className="divide-border flex max-w-[720px] flex-col divide-y border-y">
      {weekdays.map((weekday) => {
        const day = value[weekday];
        const error = dayWindowsError(day);
        const nextWindow = nextWindowAfter(day.windows);
        const toggleId = `${weekday}-enabled`;

        const updateWindow = (index: number, window: AvailabilityWindow) =>
          updateDay(weekday, {
            ...day,
            windows: day.windows.map((current, currentIndex) =>
              currentIndex === index ? window : current,
            ),
          });

        return (
          <div
            className="grid gap-2 py-4 md:grid-cols-[168px_minmax(0,1fr)] md:items-start"
            key={weekday}
          >
            <label
              className="flex items-center gap-3 text-[13px] font-medium"
              htmlFor={toggleId}
            >
              <Switch
                checked={day.enabled}
                disabled={disabled}
                id={toggleId}
                onCheckedChange={(enabled) =>
                  updateDay(weekday, {
                    enabled,
                    windows:
                      enabled && day.windows.length === 0 && nextWindow
                        ? [nextWindow]
                        : day.windows,
                  })
                }
              />
              {weekdayLabels[weekday]}
            </label>

            {day.enabled ? (
              <div className="flex flex-col gap-2">
                {day.windows.map((window, index) => (
                  <div
                    className="flex flex-wrap items-center gap-2"
                    key={index}
                  >
                    <TimeSelect
                      aria-label={`${weekdayLabels[weekday]} window ${index + 1} start`}
                      disabled={disabled}
                      onChange={(startsAt) =>
                        updateWindow(index, { ...window, startsAt })
                      }
                      value={window.startsAt}
                    />
                    <span className="text-muted-foreground text-[13px]">
                      to
                    </span>
                    <TimeSelect
                      aria-label={`${weekdayLabels[weekday]} window ${index + 1} end`}
                      disabled={disabled}
                      onChange={(endsAt) =>
                        updateWindow(index, { ...window, endsAt })
                      }
                      value={window.endsAt}
                    />
                    {day.windows.length > 1 && (
                      <Button
                        aria-label={`Remove ${weekdayLabels[weekday]} window ${index + 1}`}
                        disabled={disabled}
                        onClick={() =>
                          updateDay(weekday, {
                            ...day,
                            windows: day.windows.filter(
                              (_, currentIndex) => currentIndex !== index,
                            ),
                          })
                        }
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <X className="size-3.5" />
                      </Button>
                    )}
                  </div>
                ))}

                <Button
                  aria-label={`Add a ${weekdayLabels[weekday]} window`}
                  className="self-start"
                  disabled={disabled || !nextWindow}
                  onClick={() =>
                    nextWindow &&
                    updateDay(weekday, {
                      ...day,
                      windows: [...day.windows, nextWindow],
                    })
                  }
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  <Plus className="size-3.5" />
                  Add window
                </Button>

                {error && (
                  <p
                    className="text-destructive text-[13px] leading-[18px]"
                    role="alert"
                  >
                    {error}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-[13px] leading-8">
                No interviews
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
